'use client';

import { useState } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  BookOpen,
  Plug,
  Users,
  MessageSquare,
  Zap,
  Target,
} from 'lucide-react';

import { EmailGateStep } from '@/components/tools/email-gate-step';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 'email' | 'result';

interface ScoreBreakdown {
  category: string;
  score: number;
  maxScore: number;
  status: 'excellent' | 'good' | 'needs-work' | 'critical';
  recommendation: string;
}

export function AIReadinessAssessment() {
  const { t, isRTL } = useTranslation();
  const [step, setStep] = useState<Step>(1);
  const [volume, setVolume] = useState<string | null>(null);
  const [knowledge, setKnowledge] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [teamSize, setTeamSize] = useState<string | null>(null);
  const [automation, setAutomation] = useState<string | null>(null);

  // Step 1: Monthly ticket volume
  const VOLUME_OPTIONS = [
    { id: 'under-1k', label: t.tools_pages['Under 1,000'], score: 1 },
    { id: '1k-5k', label: t.tools_pages['1,000 - 5,000'], score: 2 },
    { id: '5k-20k', label: t.tools_pages['5,000 - 20,000'], score: 3 },
    { id: '20k-50k', label: t.tools_pages['20,000 - 50,000'], score: 4 },
    { id: 'over-50k', label: t.tools_pages['Over 50,000'], score: 5 },
  ];

  // Step 2: Knowledge base quality
  const KNOWLEDGE_OPTIONS = [
    {
      id: 'none',
      label: t.tools_pages['No documentation'],
      description: t.tools_pages['We rely on tribal knowledge'],
      score: 0,
    },
    {
      id: 'basic',
      label: t.tools_pages['Basic FAQs'],
      description: t.tools_pages['Simple FAQ page or help articles'],
      score: 1,
    },
    {
      id: 'moderate',
      label: t.tools_pages['Moderate documentation'],
      description: t.tools_pages['Help center with categorized articles'],
      score: 2,
    },
    {
      id: 'comprehensive',
      label: t.tools_pages['Comprehensive knowledge base'],
      description: t.tools_pages['Well-organized docs covering most topics'],
      score: 3,
    },
    {
      id: 'extensive',
      label: t.tools_pages['Extensive & up-to-date'],
      description:
        t.tools_pages['Regularly updated with internal + external docs'],
      score: 4,
    },
  ];

  // Step 3: Current integrations
  const INTEGRATION_OPTIONS = [
    { id: 'helpdesk', label: t.tools_pages['Helpdesk/Ticketing'], score: 2 },
    { id: 'crm', label: t.tools_pages['CRM System'], score: 2 },
    { id: 'ecommerce', label: t.tools_pages['E-commerce Platform'], score: 1 },
    { id: 'payment', label: t.tools_pages['Payment Processor'], score: 1 },
    { id: 'analytics', label: t.tools_pages['Analytics Tools'], score: 1 },
    {
      id: 'communication',
      label: t.tools_pages['Communication Tools'],
      score: 1,
    },
  ];

  // Step 4: Support channels
  const CHANNEL_OPTIONS = [
    { id: 'email', label: t.channels['Email'], score: 1 },
    { id: 'chat', label: t.channels['Web Chat'], score: 2 },
    { id: 'phone', label: t.channels['Voice AI'], score: 2 },
    { id: 'social', label: t.channels['Social'], score: 1 },
    { id: 'whatsapp', label: t.channels['WhatsApp'], score: 2 },
    { id: 'sms', label: 'SMS', score: 1 },
  ];

  // Step 5: Team structure
  const TEAM_OPTIONS = [
    {
      id: 'solo',
      label: t.tools_pages['1-2 people'],
      description: t.tools_pages['Small team wearing many hats'],
      score: 2,
    },
    {
      id: 'small',
      label: t.tools_pages['3-10 people'],
      description: t.tools_pages['Dedicated support team'],
      score: 3,
    },
    {
      id: 'medium',
      label: t.tools_pages['11-50 people'],
      description: t.tools_pages['Structured support department'],
      score: 4,
    },
    {
      id: 'large',
      label: t.tools_pages['50+ people'],
      description: t.tools_pages['Large support organization'],
      score: 5,
    },
  ];

  // Step 6: Current automation level
  const AUTOMATION_OPTIONS = [
    {
      id: 'none',
      label: t.tools_pages['No automation'],
      description: t.tools_pages['Everything is handled manually'],
      score: 5,
    },
    {
      id: 'basic',
      label: t.tools_pages['Basic automation'],
      description: t.tools_pages['Auto-replies, canned responses'],
      score: 4,
    },
    {
      id: 'moderate',
      label: t.tools_pages['Some automation'],
      description: t.tools_pages['Chatbots for simple queries'],
      score: 3,
    },
    {
      id: 'advanced',
      label: t.tools_pages['Advanced automation'],
      description: t.tools_pages['AI-powered but limited capabilities'],
      score: 2,
    },
  ];

  const toggleIntegration = (id: string) => {
    setIntegrations((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const toggleChannel = (id: string) => {
    setChannels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return volume !== null;
      case 2:
        return knowledge !== null;
      case 3:
        return integrations.length > 0;
      case 4:
        return channels.length > 0;
      case 5:
        return teamSize !== null;
      case 6:
        return automation !== null;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (step === 6) {
      setStep('email');
    } else if (typeof step === 'number') {
      setStep((step + 1) as Step);
    }
  };

  const prevStep = () => {
    if (step === 'result') {
      setStep('email');
    } else if (step === 'email') {
      setStep(6);
    } else if (typeof step === 'number' && step > 1) {
      setStep((step - 1) as Step);
    }
  };

  const calculateReadiness = () => {
    // Volume score (max 5)
    const volumeScore = VOLUME_OPTIONS.find((v) => v.id === volume)?.score || 0;

    // Knowledge score (max 4)
    const knowledgeScore =
      KNOWLEDGE_OPTIONS.find((k) => k.id === knowledge)?.score || 0;

    // Integration score (max 8)
    const integrationScore = integrations.reduce((sum, id) => {
      const option = INTEGRATION_OPTIONS.find((i) => i.id === id);
      return sum + (option?.score || 0);
    }, 0);

    // Channel score (max 9)
    const channelScore = channels.reduce((sum, id) => {
      const option = CHANNEL_OPTIONS.find((c) => c.id === id);
      return sum + (option?.score || 0);
    }, 0);

    // Team score (max 5)
    const teamScore = TEAM_OPTIONS.find((t) => t.id === teamSize)?.score || 0;

    // Automation opportunity score (max 5) - higher if less automated
    const automationScore =
      AUTOMATION_OPTIONS.find((a) => a.id === automation)?.score || 0;

    // Total possible: 36
    const totalScore =
      volumeScore +
      knowledgeScore +
      integrationScore +
      channelScore +
      teamScore +
      automationScore;
    const maxScore = 36;
    const percentage = Math.round((totalScore / maxScore) * 100);

    // Calculate grade
    let grade: 'A' | 'B' | 'C' | 'D' = 'D';
    let gradeLabel = '';
    let gradeColor = '';

    if (percentage >= 80) {
      grade = 'A';
      gradeLabel = t.tools_pages['Excellent'];
      gradeColor = 'text-green-500';
    } else if (percentage >= 60) {
      grade = 'B';
      gradeLabel = t.tools_pages['Good'];
      gradeColor = 'text-blue-500';
    } else if (percentage >= 40) {
      grade = 'C';
      gradeLabel = t.tools_pages['Moderate'];
      gradeColor = 'text-yellow-500';
    } else {
      grade = 'D';
      gradeLabel = t.tools_pages['Needs Preparation'];
      gradeColor = 'text-orange-500';
    }

    // Build breakdown
    const breakdown: ScoreBreakdown[] = [
      {
        category: t.tools_pages['Ticket Volume'],
        score: volumeScore,
        maxScore: 5,
        status:
          volumeScore >= 4
            ? 'excellent'
            : volumeScore >= 3
              ? 'good'
              : volumeScore >= 2
                ? 'needs-work'
                : 'critical',
        recommendation:
          volumeScore >= 3
            ? t.tools_pages[
                'Great volume for AI ROI. You will see significant savings.'
              ]
            : t.tools_pages['AI can still help, but ROI builds as you scale.'],
      },
      {
        category: t.tools_pages['Knowledge Base'],
        score: knowledgeScore,
        maxScore: 4,
        status:
          knowledgeScore >= 3
            ? 'excellent'
            : knowledgeScore >= 2
              ? 'good'
              : knowledgeScore >= 1
                ? 'needs-work'
                : 'critical',
        recommendation:
          knowledgeScore >= 2
            ? t.tools_pages['Good foundation for AI training.']
            : t.tools_pages[
                'Consider building documentation—AI learns from your content.'
              ],
      },
      {
        category: t.tools_pages['Integrations'],
        score: Math.min(integrationScore, 8),
        maxScore: 8,
        status:
          integrationScore >= 6
            ? 'excellent'
            : integrationScore >= 4
              ? 'good'
              : integrationScore >= 2
                ? 'needs-work'
                : 'critical',
        recommendation:
          integrationScore >= 4
            ? t.tools_pages['Well-connected stack. AI can take real actions.']
            : t.tools_pages['More integrations = more AI capabilities.'],
      },
      {
        category: t.tools_pages['Channel Coverage'],
        score: Math.min(channelScore, 9),
        maxScore: 9,
        status:
          channelScore >= 6
            ? 'excellent'
            : channelScore >= 4
              ? 'good'
              : channelScore >= 2
                ? 'needs-work'
                : 'critical',
        recommendation:
          channelScore >= 4
            ? t.tools_pages[
                'Multi-channel presence. AI can unify your support.'
              ]
            : t.tools_pages['Consider expanding channels—AI handles them all.'],
      },
      {
        category: t.tools_pages['Team Scale'],
        score: teamScore,
        maxScore: 5,
        status:
          teamScore >= 4
            ? 'excellent'
            : teamScore >= 3
              ? 'good'
              : teamScore >= 2
                ? 'needs-work'
                : 'critical',
        recommendation:
          teamScore >= 3
            ? t.tools_pages[
                'Team can focus on complex issues while AI handles routine.'
              ]
            : t.tools_pages['AI can be your force multiplier.'],
      },
      {
        category: t.tools_pages['Automation Opportunity'],
        score: automationScore,
        maxScore: 5,
        status:
          automationScore >= 4
            ? 'excellent'
            : automationScore >= 3
              ? 'good'
              : automationScore >= 2
                ? 'needs-work'
                : 'critical',
        recommendation:
          automationScore >= 3
            ? t.tools_pages['High opportunity! Lots of room to automate.']
            : t.tools_pages[
                'You have some automation. AI can take it further.'
              ],
      },
    ];

    return {
      totalScore,
      maxScore,
      percentage,
      grade,
      gradeLabel,
      gradeColor,
      breakdown,
    };
  };

  const results = step === 'result' ? calculateReadiness() : null;

  const getStatusIcon = (status: ScoreBreakdown['status']) => {
    switch (status) {
      case 'excellent':
        return <CheckCircle2 className="size-5 text-green-500" />;
      case 'good':
        return <CheckCircle2 className="size-5 text-blue-500" />;
      case 'needs-work':
        return <AlertCircle className="size-5 text-yellow-500" />;
      case 'critical':
        return <AlertCircle className="size-5 text-orange-500" />;
    }
  };

  const getStatusColor = (status: ScoreBreakdown['status']) => {
    switch (status) {
      case 'excellent':
        return 'bg-green-500';
      case 'good':
        return 'bg-blue-500';
      case 'needs-work':
        return 'bg-yellow-500';
      case 'critical':
        return 'bg-orange-500';
    }
  };

  return (
    <section className="min-h-screen bg-white pt-32 pb-20 md:pt-40">
      <div className="container max-w-3xl">
        {/* Progress */}
        {step !== 'result' && step !== 'email' && (
          <div className="mb-12">
            <div className="mb-2 flex justify-between text-sm text-neutral-400">
              <span>
                {t.tools_pages['Step']} {step} {t.tools_pages['of 6']}
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-neutral-100">
              <motion.div
                className="h-full bg-black"
                initial={{ width: 0 }}
                animate={{ width: `${((step as number) / 6) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Step 1: Volume */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-700">
                  <Target className="size-4" />
                  {t.tools_pages['AI Readiness Assessment']}
                </div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {
                    t.tools_pages[
                      'How many support tickets do you handle monthly?'
                    ]
                  }
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'This helps us understand the scale of your support operations.'
                    ]
                  }
                </p>
              </div>

              <div className="grid gap-4">
                {VOLUME_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setVolume(option.id)}
                    className={`flex items-center justify-between rounded-2xl border-2 p-6 transition-all ${
                      volume === option.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          volume === option.id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <TrendingUp className="size-5" />
                      </div>
                      <span className="font-semibold text-black">
                        {option.label}
                      </span>
                    </div>
                    {volume === option.id && (
                      <Check className="size-6 text-black" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Knowledge Base */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {t.tools_pages['How mature is your knowledge base?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'AI learns from your documentation to provide accurate answers.'
                    ]
                  }
                </p>
              </div>

              <div className="grid gap-4">
                {KNOWLEDGE_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setKnowledge(option.id)}
                    className={`flex items-center justify-between rounded-2xl border-2 p-6 transition-all ${
                      knowledge === option.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          knowledge === option.id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <BookOpen className="size-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-black">
                          {option.label}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {option.description}
                        </div>
                      </div>
                    </div>
                    {knowledge === option.id && (
                      <Check className="size-6 text-black" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Integrations */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {t.tools_pages['What systems do you currently use?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'Select all that apply. More integrations = more AI capabilities.'
                    ]
                  }
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {INTEGRATION_OPTIONS.map((option) => {
                  const isSelected = integrations.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => toggleIntegration(option.id)}
                      className={`relative flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border-2 p-4 text-center transition-all ${
                        isSelected
                          ? 'border-black bg-neutral-50'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div
                        className={`absolute top-3 right-3 flex size-5 items-center justify-center rounded-full transition-all ${
                          isSelected ? 'bg-black' : 'bg-transparent'
                        }`}
                      >
                        <Check
                          className={`size-3 transition-opacity ${isSelected ? 'text-white opacity-100' : 'opacity-0'}`}
                        />
                      </div>
                      <div
                        className={`flex size-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                          isSelected ? 'bg-black text-white' : 'bg-neutral-100'
                        }`}
                      >
                        <Plug className="size-5" />
                      </div>
                      <span className="text-sm leading-tight font-medium text-black">
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Step 4: Channels */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {t.tools_pages['Which support channels do you offer?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'Select all channels where customers can reach you.'
                    ]
                  }
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {CHANNEL_OPTIONS.map((option) => {
                  const isSelected = channels.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => toggleChannel(option.id)}
                      className={`relative flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border-2 p-4 text-center transition-all ${
                        isSelected
                          ? 'border-black bg-neutral-50'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div
                        className={`absolute top-3 right-3 flex size-5 items-center justify-center rounded-full transition-all ${
                          isSelected ? 'bg-black' : 'bg-transparent'
                        }`}
                      >
                        <Check
                          className={`size-3 transition-opacity ${isSelected ? 'text-white opacity-100' : 'opacity-0'}`}
                        />
                      </div>
                      <div
                        className={`flex size-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                          isSelected ? 'bg-black text-white' : 'bg-neutral-100'
                        }`}
                      >
                        <MessageSquare className="size-5" />
                      </div>
                      <span className="text-sm leading-tight font-medium text-black">
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Step 5: Team Size */}
          {step === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {t.tools_pages['How large is your support team?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'This helps us understand your operational scale.'
                    ]
                  }
                </p>
              </div>

              <div className="grid gap-4">
                {TEAM_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setTeamSize(option.id)}
                    className={`flex items-center justify-between rounded-2xl border-2 p-6 transition-all ${
                      teamSize === option.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          teamSize === option.id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <Users className="size-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-black">
                          {option.label}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {option.description}
                        </div>
                      </div>
                    </div>
                    {teamSize === option.id && (
                      <Check className="size-6 text-black" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 6: Current Automation */}
          {step === 6 && (
            <motion.div
              key="step6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {t.tools_pages["What's your current automation level?"]}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'This helps us identify your automation opportunity.'
                    ]
                  }
                </p>
              </div>

              <div className="grid gap-4">
                {AUTOMATION_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setAutomation(option.id)}
                    className={`flex items-center justify-between rounded-2xl border-2 p-6 transition-all ${
                      automation === option.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          automation === option.id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <Zap className="size-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-black">
                          {option.label}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {option.description}
                        </div>
                      </div>
                    </div>
                    {automation === option.id && (
                      <Check className="size-6 text-black" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Email Gate */}
          {step === 'email' && (
            <EmailGateStep
              key="email"
              tool="AI Readiness Assessment"
              onSubmit={() => setStep('result')}
              onBack={prevStep}
            />
          )}

          {/* Result */}
          {step === 'result' && results && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-700">
                  <Sparkles className="size-4" />
                  {t.tools_pages['Your AI Readiness Score']}
                </div>
                <h1 className="text-4xl md:text-5xl text-black">
                  {t.tools_pages['Assessment Complete']}
                </h1>
              </div>

              {/* Main score highlight */}
              <div className="rounded-3xl bg-black p-10 text-center text-white">
                <div className="mb-2 flex items-center justify-center gap-4">
                  <span
                    className={`text-8xl font-bold tracking-tight ${results.gradeColor}`}
                  >
                    {results.grade}
                  </span>
                </div>
                <div className="text-2xl font-semibold text-white">
                  {results.gradeLabel}
                </div>
                <div className="mt-4 text-lg text-neutral-400">
                  {results.percentage}% {t.tools_pages['AI Ready']}
                </div>
                <div className="mx-auto mt-4 h-2 max-w-xs overflow-hidden rounded-full bg-white/20">
                  <motion.div
                    className="h-full bg-white"
                    initial={{ width: 0 }}
                    animate={{ width: `${results.percentage}%` }}
                    transition={{ duration: 1, delay: 0.3 }}
                  />
                </div>
              </div>

              {/* Summary message */}
              <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-6">
                <p className="text-center text-lg text-neutral-700">
                  {results.percentage >= 80 ? (
                    <>
                      <strong>
                        {t.tools_pages["You're highly ready for AI support!"]}
                      </strong>{' '}
                      {
                        t.tools_pages[
                          'Your infrastructure, documentation, and scale make you an ideal candidate. You could see ROI within weeks.'
                        ]
                      }
                    </>
                  ) : results.percentage >= 60 ? (
                    <>
                      <strong>
                        {
                          t.tools_pages[
                            "You're well-positioned for AI support."
                          ]
                        }
                      </strong>{' '}
                      {
                        t.tools_pages[
                          "With a few improvements, you'll maximize your automation potential. Let's discuss your roadmap."
                        ]
                      }
                    </>
                  ) : results.percentage >= 40 ? (
                    <>
                      <strong>
                        {t.tools_pages['You have a solid foundation.']}
                      </strong>{' '}
                      {
                        t.tools_pages[
                          'Some areas need attention before going live, but AI can still drive significant value.'
                        ]
                      }
                    </>
                  ) : (
                    <>
                      <strong>
                        {t.tools_pages["Let's build your foundation together."]}
                      </strong>{' '}
                      {
                        t.tools_pages[
                          "We'll help you prepare your documentation and systems for AI success."
                        ]
                      }
                    </>
                  )}
                </p>
              </div>

              {/* Detailed breakdown */}
              <div className="space-y-4">
                <h3 className="text-xl text-black">
                  {t.tools_pages['Detailed Breakdown']}
                </h3>
                {results.breakdown.map((item) => (
                  <div
                    key={item.category}
                    className="overflow-hidden rounded-2xl border border-neutral-200"
                  >
                    <div className="flex items-center justify-between p-4 bg-white">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(item.status)}
                        <div className={isRTL ? 'text-right' : 'text-left'}>
                          <div className="font-semibold text-black">
                            {item.category}
                          </div>
                          <div className="text-sm text-neutral-500">
                            {item.recommendation}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-lg font-semibold text-black">
                          {item.score}/{item.maxScore}
                        </div>
                      </div>
                    </div>
                    <div className="h-1 bg-neutral-100">
                      <motion.div
                        className={`h-full ${getStatusColor(item.status)}`}
                        initial={{ width: 0 }}
                        animate={{
                          width: `${(item.score / item.maxScore) * 100}%`,
                        }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Next steps */}
              <div className="rounded-2xl border-2 border-dashed border-green-200 bg-green-50 p-6">
                <div
                  className={`flex items-start gap-4 ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-green-100">
                    <Sparkles className="size-5 text-green-600" />
                  </div>
                  <div className={isRTL ? 'text-right' : 'text-left'}>
                    <h3 className="text-neutral-900">
                      {t.tools_pages["Ready to see what's possible?"]}
                    </h3>
                    <p className="mt-1 text-sm text-neutral-600">
                      {
                        t.tools_pages[
                          'Based on your assessment, we estimate you could automate'
                        ]
                      }{' '}
                      <span className="font-semibold">
                        {results.percentage >= 60 ? '70-80%' : '50-70%'}
                      </span>{' '}
                      {
                        t.tools_pages[
                          "of your support tickets with Open. Let's run the numbers together."
                        ]
                      }
                    </p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <Link
                        href="/tools/roi-calculator"
                        prefetch={true}
                        className="inline-flex items-center gap-1 text-sm font-medium text-green-600 hover:text-green-700"
                      >
                        {t.tools_pages['Calculate your ROI']}
                        <ArrowRight
                          className={`size-4 ${isRTL ? 'rotate-180' : ''}`}
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <Link
                href="/demo"
                prefetch={true}
                aria-label={t.common['Learn more about Open and book a demo']}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-black py-4 text-lg font-semibold text-white transition-all hover:bg-neutral-800"
              >
                {t.tools_pages['Get started now']}
                <ArrowRight className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        {step !== 'result' && step !== 'email' && (
          <div className="mt-12 flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={step === 1}
              className={`flex items-center gap-2 rounded-full px-6 py-3 font-medium transition-all ${
                step === 1
                  ? 'cursor-not-allowed text-neutral-300'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <ArrowLeft className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
              {t.tools_pages['Back']}
            </button>

            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className={`flex items-center gap-2 rounded-full px-8 py-3 font-semibold transition-all ${
                canProceed()
                  ? 'bg-black text-white hover:bg-neutral-800'
                  : 'cursor-not-allowed bg-neutral-200 text-neutral-400'
              }`}
            >
              {step === 6
                ? t.tools_pages['See my results']
                : t.tools_pages['Continue']}
              <ArrowRight className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}

        {step === 'result' && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setStep(1)}
              className="text-neutral-500 underline hover:text-neutral-700"
            >
              {t.tools_pages['Start over']}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
